/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, ReactNode, useEffect } from "react";
import NoFooterModal from "../NoFooterModal";
import { useFormattedMsg } from "../../../../hooks/useFormatMsg";
import { ErrorEntity } from "../../../../store/bugReport/types";
import SingleBugOverview from "./SingleBugOverview";
import { bugsOverviewContainerCss } from "./styles";
import { useCommonInvokeBugReportModalCallback } from "../../../../store/bugReport/useActions";

export type BugsOverviewModalProps = {
  isModalOpen: boolean;
  toggleModal: () => void;
  errorsStack: ErrorEntity[];
};

const BugsOverviewModal: FC<BugsOverviewModalProps> = ({
  isModalOpen,
  toggleModal,
  errorsStack,
}) => {
  const modalTitle = useFormattedMsg("bugs_overview.modal.title");
  const invokeBugReportModal = useCommonInvokeBugReportModalCallback();

  useEffect(() => {
    if (errorsStack?.length === 0 && isModalOpen) {
      toggleModal();
    }
  }, [errorsStack, isModalOpen, toggleModal]);

  const renderEachError = (
    errorEntity: ErrorEntity,
    index: number
  ): ReactNode => {
    const { handledBy, errorData, timeOfError, bugReported } = errorEntity;

    const invokeBugModal = (): void =>
      invokeBugReportModal(handledBy, timeOfError, errorData);

    if (Array.isArray(errorData)) {
      return errorData.map((singleError, i) => (
        <SingleBugOverview
          bugName={singleError.name}
          bugMessage={singleError.message}
          invokeBugModal={invokeBugModal}
          bugReported={bugReported}
          stack={singleError.stack}
          componentStack={singleError.componentStack}
          key={timeOfError}
          index={index}
        />
      ));
    }

    return (
      <SingleBugOverview
        bugMessage={errorData.message}
        bugName={errorData.name}
        invokeBugModal={invokeBugModal}
        bugReported={bugReported}
        stack={errorData.stack}
        componentStack={errorData.componentStack}
        key={timeOfError}
        index={index}
      />
    );
  };

  return (
    <NoFooterModal isOpen={isModalOpen} toggle={toggleModal} title={modalTitle}>
      <div css={bugsOverviewContainerCss}>
        {errorsStack.map(renderEachError)}
      </div>
    </NoFooterModal>
  );
};

export default BugsOverviewModal;
